import { useState } from 'react';
import { Customer, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import Loading from '../../../../../components/loading/Loading';
import { EditType } from '../item/EditType';
import { BookedItemPanelHeader } from '../panels/BookedItemPanelHeader';
import Service from './Service';
import Create from './Create';
import SmartSendOrderDetails from './SmartSendOrderDetails';
import Csv from './Csv';
import Restore from './Restore';
import ItemDetails from './ItemDetails';
import { uid } from '../../../lib/helpers/uid';
import OrderDetails from './OrderDetails';
import { useEventEmitter } from '../../../lib/em/Hooks';
import { Consts, EditEventType } from '../../../lib/em/EmitTypes';
import { SmartSendItemsCache } from '../../../lib/cache/SmartSendItemsCache';
import { Tagged } from '../../Tagged';
import ItemRules from './ItemRules';
import { P2GSidePanel } from '~/components/sidepanel';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { GenerateEnquryLink } from '~/apps/smartsend/lib/go-helpers';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { TenantSettingKeys } from '~/lib/consts/tenant-setting-keys';
import { MapAddressToCustomer } from '../../address/MapAddressLookupToCustomer';
import useProviderName from '../../../hooks/use-provider-name';

export interface GenericEditProps {
  item: SmartSendItemApiModel;
  editType: EditType;
  onClose: () => void;
  reset?: (e: EditType) => void;
}

interface props {
  getItem?: (id: string) => SmartSendItemApiModel;
}

export default function Edit(props: props) {
  const { settings } = useTenant();
  const providerName = useProviderName();
  const width = '700px';
  const [item, setItem] = useState<SmartSendItemApiModel>();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(uid());
  const [editType, setEditType] = useState<EditType>(null);
  const [sendTo, setSendTo] = useState<Customer>();
  const { goTo } = useGoto();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const { useListener } = useEventEmitter<EditEventType>();
  useListener(
    Consts.Edit,
    (x: EditEventType) => {
      if (x.Id) {
        const i = getItem(x.Id);
        setItem(JSON.parse(JSON.stringify(i)));
        setKey(uid());
        setOpen(true);
      } else {
        setOpen(false);
        setItem(null);
      }
      if (x.SendTo) {
        const c = MapAddressToCustomer(x.SendTo);
        setSendTo(c);
      }
      setEditType(x.Action);
    },
    []
  );

  const onClose = () => {
    setOpen(false);
    setItem(null);
    setEditType(null);
    setSendTo(null);
  };

  const onCancel = () => {
    setOpen(false);
    goTo(`${RoutingConstants.Cancellations}/request/${item?.BookingDetails?.OrderLineId}`);
    setItem(null);
    setEditType(null);
    setSendTo(null);
  };

  const onRaiseEnquiry = () => {
    setOpen(false);
    goTo(GenerateEnquryLink(item?.BookingDetails?.OrderLineId));
    setItem(null);
    setEditType(null);
    setSendTo(null);
  };

  const reset = (e: EditType) => {
    const i = getItem(item.Id);
    setItem(JSON.parse(JSON.stringify(i)));
    setEditType(e);
    setKey(uid());
    setSendTo(null);
  };

  const getItem = (id: string) => {
    return props.getItem ? props.getItem(id) : SmartSendItemsCache.find((x) => x.Id === id);
  };

  if (editType === 'CSV') {
    return <Csv item={null} onClose={onClose} editType={editType} />;
  }

  if (editType === 'RESTORE') {
    return <Restore item={null} onClose={onClose} editType={editType} />;
  }

  if (editType === 'CREATE') {
    return <Create item={null} onClose={onClose} editType={editType} sendTo={sendTo} />;
  }

  if (!item) {
    return null;
  }

  if (editType === 'SERVICE') {
    return <Service onClose={onClose} item={item} editType={editType} />;
  }

  if (editType.startsWith('SMARTSEND-ORDER-')) {
    return (
      <P2GSidePanel
        open={open}
        onClose={onClose}
        anchor={'right'}
        title={BookedItemPanelHeader(item?.BookingDetails, providerName, settings[TenantSettingKeys.OrderLinePrefix])}
        width={width}
      >
        <Tagged tag="panel-item-details">
          {item ? (
            <SmartSendOrderDetails
              onRaiseEnquiry={onRaiseEnquiry}
              onClose={onClose}
              onCancel={onCancel}
              item={item}
              editType={editType}
              getRawItem={getItem}
            />
          ) : (
            <Loading />
          )}
        </Tagged>
      </P2GSidePanel>
    );
  }

  if (editType.startsWith('ORDER-')) {
    return (
      <P2GSidePanel
        open={open}
        onClose={onClose}
        anchor={'right'}
        title={BookedItemPanelHeader(item?.BookingDetails, providerName, settings[TenantSettingKeys.OrderLinePrefix])}
        width={width}
      >
        <Tagged tag="panel-order-details">
          {item ? (
            <OrderDetails
              onCancel={onCancel}
              onRaiseEnquiry={onRaiseEnquiry}
              onClose={onClose}
              item={item}
              editType={editType}
              getRawItem={getItem}
            />
          ) : (
            <Loading />
          )}
        </Tagged>
      </P2GSidePanel>
    );
  }

  if (editType == 'RULES') {
    return (
      <P2GSidePanel
        open={open}
        onClose={onClose}
        anchor={'right'}
        title={t('RULES_SIDEPANEL_TTILE', { defaultValue: 'Rules' })}
        width={width}
      >
        <Tagged tag="panel-order-rules">
          {item ? <ItemRules item={item} editType={editType} onClose={onClose} /> : <Loading />}
        </Tagged>
      </P2GSidePanel>
    );
  }

  return (
    <P2GSidePanel
      open={open}
      onClose={onClose}
      anchor={'right'}
      title={t('ORDER_DETAILS_SIDEPANEL_TITLE', { defaultValue: 'Order Details' })}
      width={width}
    >
      <Tagged tag="panel-edit">
        {item ? <ItemDetails key={key} reset={reset} onClose={onClose} item={item} editType={editType} /> : <Loading />}
      </Tagged>
    </P2GSidePanel>
  );
}
